import React, { FC } from 'react';

import { LucidTheme } from '../../types/types';
import Heading from '../heading/heading';
import Box from '../box/box';
import Container from '../container/container';
import ImageWithPlaceholder from '../image/image-with-placeholder';
import Text from '../text/text';
import Link from '../link/link';
import { ComponentImage } from '../../types/generics';

export interface SimpleQuoteProps {
  /** Simple heading above the quote. */
  heading?: string;
  /** The author of the quote. */
  attribution: string;
  /** The logo of the company for the quote. */
  image?: ComponentImage;
  /** The content of the quote. */
  quote: string;
  /** The date of the quote. */
  date?: string;
  /** Extra padding number to pass to the Container component */
  extraPadding?: number;
  /** Link after the quote attribution - structured like a link from Drupal */
  link?: {
    /** Link text */
    title?: string;
    /** Link path structured like a Drupal Link */
    url?: {
      path?: string;
    };
  };
}

const SimpleQuote: FC<SimpleQuoteProps> = ({ heading, attribution, image, quote, date, extraPadding, link }) => (
  <Container extraPadding={extraPadding}>
    <Box my={{ default: 6, tablet: 10 }}>
      {heading && (
        <Heading as="h2" variant="headlineXL">
          {heading}
        </Heading>
      )}
      <Box alignItems="center" display="flex" flexWrap="wrap" justifyContent="space-between">
        {image && image.imageSrc && (
          <Box flex={{ default: '1 1 100%', tabletSmall: '0 1 25%' }} mb={{ default: 5, tabletLandscape: 0 }}>
            <ImageWithPlaceholder alt={image.imageAlt || ''} css={{ maxHeight: '75px' }} src={image.imageSrc} />
          </Box>
        )}
        <Box
          flex={{
            default: '1 1 100%',
            tabletSmall: `0 1 ${image ? '70%' : '100%'}`,
          }}
        >
          <Text variant="paragraphXL300">
            &quot;
            {quote}
            &quot;
          </Text>
          <Box alignItems="center" display="flex" flexWrap="wrap" mt={3}>
            <Text
              css={(theme: LucidTheme) => [
                {
                  fontWeight: theme.fontWeights.strong,
                },
                link && { marginRight: theme.space.s120 },
              ]}
            >
              -&nbsp;
              {attribution}
              {date && (
                <span
                  css={(theme: LucidTheme) => ({
                    color: theme.colors.coolGrey[60],
                  })}
                >
                  &nbsp;|&nbsp;
                  {date}
                </span>
              )}
            </Text>
            {link && (
              <Link hasArrow to={link.url?.path}>
                {link.title}
              </Link>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  </Container>
);

export { SimpleQuote };
export default SimpleQuote;
